import { BaseModule } from '../../base/module/base-module'
import {
    TAllIntegrations,
    TIntegration,
    TIntegrationEventData,
    TIntegrationMode,
    TSecurityServiceState,
} from '../../modules/integrations/integrations'
import { SecurityServiceStateEnum } from '@atbay/productivity-integrations-node-sdk'

export interface TIntegrationProvider {
    id: string
    type: TAllIntegrations
    displayName: string
    loading: boolean
    integrations: TIntegration[]
    integrationMode: TIntegrationMode
    integrationEventData: TIntegrationEventData | null
}

export interface TIntegrationProviders {
    [key: string]: TIntegrationProvider
}
export interface IIntegrationsState extends IRemoteData {
    providers: TIntegrationProviders
    pendingIntegrationProvidersIds: string[]
    integrationEventData: TIntegrationEventData | null
    isScansLoading: boolean
}

const state = {
    loading: false,
    error: false,
    lastFetched: '',
    providers: {},
    pendingIntegrationProvidersIds: [],
    integrationEventData: null,
    isScansLoading: false,
} satisfies IIntegrationsState

const FRAUD_DEFENSE_INTEGRATION_TYPES = [
    'ms_fraud_defense',
    'google_fraud_defense',
]

export class Integrations extends BaseModule<IIntegrationsState> {
    constructor(state: IIntegrationsState) {
        super(state, 'integrations.store')

        this.$eventBus.on('pending_integrations_provider_ids', async (args) => {
            this.setPendingIntegrationProvidersIds(args.providerIds)
        })

        this.$eventBus.on(
            'security_configurations_scan_loading',
            ({ isLoading }) => {
                this.setIsScansLoading(isLoading)
            }
        )
    }

    getStore(): IIntegrationsState {
        return this.store.get()
    }

    getPendingIntegrationProvidersIds() {
        const { pendingIntegrationProvidersIds } = this.useStore()
        return pendingIntegrationProvidersIds
    }

    setPendingIntegrationProvidersIds(
        pendingIntegrationProvidersIds: string[]
    ) {
        this.store.setKey(
            'pendingIntegrationProvidersIds',
            pendingIntegrationProvidersIds
        )
    }

    setIsScansLoading(isScansLoading: boolean) {
        this.store.setKey('isScansLoading', isScansLoading)
    }

    getProviderById(id: string): TIntegrationProvider | undefined {
        const { providers } = this.useStore()
        const provider = providers[id]
        if (provider) {
            return provider
        }
        return undefined
    }

    updateProviderProperty(
        id: string,
        key: string,
        value: string | number | boolean
    ) {
        const providers = {
            ...this.store.get().providers,
        } satisfies TIntegrationProviders
        const provider = providers[id]
        if (provider) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // todo : fix types
            provider[key] = value
        }
        this.store.setKey('providers', providers)
    }

    updateSecurityServiceState(
        providerId: string,
        integrationId: string,
        serviceId: string,
        state: TSecurityServiceState
    ) {
        const providers = {
            ...this.store.get().providers,
        } satisfies TIntegrationProviders

        const provider = providers[providerId]
        if (!provider) return

        const integration = provider.integrations.find(
            (integration) => integration.integration_id === integrationId
        )
        if (!integration) return

        const services = integration.security_services ?? []
        const updatedServices = services.map((service) =>
            service.id === serviceId ? { ...service, state } : service
        )

        const updatedIntegration = {
            ...integration,
            security_services: updatedServices,
        }

        const updatedIntegrations = provider.integrations.map((integration) =>
            integration.integration_id === integrationId
                ? updatedIntegration
                : integration
        )

        this.store.setKey('providers', {
            ...providers,
            [providerId]: {
                ...provider,
                integrations: updatedIntegrations,
            },
        })
    }

    getIntegrationsByType(type: TAllIntegrations): TIntegration[] {
        const { providers } = this.useStore()

        return (
            Object.values(providers).find((provider) => provider.type === type)
                ?.integrations ?? []
        )
    }

    getIntegrationByIntegrationId(
        integrationId: string
    ): TIntegration | undefined {
        const { providers } = this.store.get()

        return Object.values(providers)
            .flatMap((provider) => provider.integrations)
            .find((integration) => integration.integration_id === integrationId)
    }

    getActiveIntegrationsByType(type: TAllIntegrations): TIntegration[] {
        const integrations = this.getIntegrationsByType(type)
        return integrations.filter(
            (integration) => integration.status === 'active'
        )
    }

    getActiveAndBrokenIntegrationsByType(
        type: TAllIntegrations
    ): TIntegration[] {
        const integrations = this.getIntegrationsByType(type)
        return integrations.filter(
            (integration) =>
                integration.status === 'active' ||
                integration.status === 'broken'
        )
    }

    getBrokenIntegrationsByType(type: TAllIntegrations): TIntegration[] {
        const integrations = this.getIntegrationsByType(type)
        return integrations.filter(
            (integration) => integration.status === 'broken'
        )
    }

    isIntegrationActiveByType(type: TAllIntegrations): boolean {
        const integrations = this.getIntegrationsByType(type)
        return (
            integrations.filter(
                (integration) => integration.status === 'active'
            ).length > 0
        )
    }

    setProviders(providers: TIntegrationProviders) {
        this.store.setKey('providers', providers)
    }

    getProviderByType(
        type: TAllIntegrations
    ): TIntegrationProvider | undefined {
        const { providers } = this.useStore()
        return Object.values(providers).find(
            (provider) => provider.type === type
        )
    }

    getFraudDefenseIntegrations({
        onlyActive = false,
        onlyBroken = false,
    }: { onlyActive?: boolean; onlyBroken?: boolean } = {}): TIntegration[] {
        const { providers } = this.useStore()
        const fraudDefenseProviders = Object.values(providers).filter(
            (provider) =>
                FRAUD_DEFENSE_INTEGRATION_TYPES.includes(provider.type)
        )

        const integrations = fraudDefenseProviders
            .map((provider) => provider.integrations)
            .flat()

        if (onlyActive) {
            return integrations.filter(
                (integration) => integration.status === 'active'
            )
        }
        if (onlyBroken) {
            return integrations.filter(
                (integration) => integration.status === 'broken'
            )
        }
        return integrations
    }

    getNumberOfFdEnabledSecurityServices = () => {
        const googleFdEnabledSecurityServices =
            this.getActiveIntegrationsByType('google_fraud_defense')
                .flatMap((integration) => integration.security_services ?? [])
                .filter(
                    (service) =>
                        service.name === 'fd' &&
                        service.state === SecurityServiceStateEnum.ENABLED
                ).length

        const microsoftFdEnabledSecurityServices =
            this.getActiveIntegrationsByType('ms_fraud_defense')
                .flatMap((integration) => integration.security_services ?? [])
                .filter(
                    (service) =>
                        service.name === 'fd' &&
                        service.state === SecurityServiceStateEnum.ENABLED
                ).length

        return (
            googleFdEnabledSecurityServices + microsoftFdEnabledSecurityServices
        )
    }

    getNumberOfDisabledFDSecurityServices = () => {
        const googleFdDisabledSecurityServices =
            this.getActiveIntegrationsByType('google_fraud_defense')
                .flatMap((integration) => integration.security_services ?? [])
                .filter(
                    (service) =>
                        service.name === 'fd' &&
                        service.state === SecurityServiceStateEnum.DISABLED
                ).length

        const microsoftFdDisabledSecurityServices =
            this.getActiveIntegrationsByType('ms_fraud_defense')
                .flatMap((integration) => integration.security_services ?? [])
                .filter(
                    (service) =>
                        service.name === 'fd' &&
                        service.state === SecurityServiceStateEnum.DISABLED
                ).length

        return (
            googleFdDisabledSecurityServices +
            microsoftFdDisabledSecurityServices
        )
    }

    areIntegrationSecurityServicesAllDisabled(
        integration?: TIntegration
    ): boolean {
        if (!integration) return false
        const services = integration.security_services ?? []
        if (services.length === 0) return false
        return services.every(
            (service) => service.state === SecurityServiceStateEnum.DISABLED
        )
    }

    areAllFraudDefenseSecurityServicesDisabled(): boolean {
        const integrations = this.getFraudDefenseIntegrations({
            onlyActive: true,
        })
        const services = integrations
            .flatMap((integration) => integration.security_services ?? [])
            .filter((service) => service.name === 'fd')
        if (services.length === 0) return false
        return services.every(
            (service) => service.state === SecurityServiceStateEnum.DISABLED
        )
    }

    hasAtLeastOneActiveIntegrationWithDisabledFdService(): boolean {
        const activeIntegrations = this.getFraudDefenseIntegrations({
            onlyActive: true,
        })
        const services = activeIntegrations
            .flatMap((integration) => integration.security_services ?? [])
            .filter((service) => service.name === 'fd')
        return services.some(
            (service) => service.state === SecurityServiceStateEnum.DISABLED
        )
    }

    setLastFetched(lastFetched: number | string): void {
        this.store.setKey('lastFetched', lastFetched)
    }

    setLoading(loading: boolean): void {
        this.store.setKey('loading', loading)
    }

    setError(error: boolean): void {
        this.store.setKey('error', error)
    }
    setIntegrationEventData(
        integrationEventData: TIntegrationEventData | null
    ): void {
        this.store.setKey('integrationEventData', integrationEventData)
    }

    getIntegrationEventData(): TIntegrationEventData | null {
        return this.getStore().integrationEventData
    }

    isFDBroken(): boolean {
        return (
            this.getBrokenIntegrationsByType('google_fraud_defense').length >
                0 ||
            this.getBrokenIntegrationsByType('ms_fraud_defense').length > 0
        )
    }

    hasBrokenIntegrationWithEnabledFdService(): boolean {
        const fdIntegrations = this.getFraudDefenseIntegrations({
            onlyBroken: true,
        })
        const services = fdIntegrations
            .flatMap((integration) => integration.security_services ?? [])
            .filter((service) => service.name === 'fd')
        return services.some(
            (service) => service.state === SecurityServiceStateEnum.ENABLED
        )
    }
}

export const IntegrationsStore = new Integrations(state)
